import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setFocusField } from './v2Upload.slice';
import { Missing } from './utils/validation';

interface FieldProps {
  'data-focus': string;
  'data-invalid'?: true;
  onBlur: () => void;
}

export interface FieldErrors {
  /** True once this field has been left empty, or Continue was pressed. */
  bad: (field: string) => boolean;
  /** For the field's outer wrapper: the jump target, the invalid flag, and the
   * blur that arms it. React's onBlur bubbles from the control inside. The red
   * itself goes on the control — see redlineControl / redlineGroup. */
  props: (field: string) => FieldProps;
  /** Call from Continue: false means it blocked, having redlined every gap and
   * scrolled to the first. */
  guard: () => boolean;
}

/** Live per-field validation. A field redlines once the user has left it, and
 * pressing Continue reveals every remaining gap at once — so a required field
 * nobody ever visited still gets named. */
export default function useFieldErrors(missing: Missing[]): FieldErrors {
  const dispatch = useDispatch();
  const [touched, setTouched] = useState<string[]>([]);

  const gaps = useMemo(
    () =>
      new Set(
        missing
          .map((m) => m.field)
          .filter((field): field is string => Boolean(field))
      ),
    [missing]
  );

  const bad = useCallback(
    (field: string) => gaps.has(field) && touched.includes(field),
    [gaps, touched]
  );

  const props = useCallback(
    (field: string): FieldProps => ({
      'data-focus': field,
      'data-invalid': bad(field) || undefined,
      onBlur: () => setTouched((t) => (t.includes(field) ? t : [...t, field])),
    }),
    [bad]
  );

  const guard = useCallback(() => {
    if (missing.length === 0) return true;
    setTouched((t) => [...new Set([...t, ...gaps])]);
    const first = missing.find((m) => m.field);
    if (first?.field) dispatch(setFocusField(first.field));
    return false;
  }, [missing, gaps, dispatch]);

  return { bad, props, guard };
}
